import { AntDesign } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View, FlatList, Pressable, TextInput } from "react-native";

import AdminReceiptModal from "../modal/admin/AdminReceiptModal";

import i18n from "@/locales/i18n";
import {
  checkAssetPrecision,
  checkAsset,
} from "@/utils/transactions/ExchangeCurrencies";
import { formatToFloat } from "@/utils/transactions/FormatTransactionValue";

interface TransactionsItemsProps {
  data: any;
}

export default function TransactionsItems(props: TransactionsItemsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetails, setShowDetails] = useState(false);
  const [currentTransactionInfo, setCurrentTransactionInfo] = useState({});
  const { t } = useTranslation();
  const pageLimit = 20;

  const status = {
    AUTHORIZED: t("common:status.authorized"),
    PENDING: t("common:status.pending"),
    POSTED: t("common:status.posted"),
    CANCELLED: t("common:status.cancelled"),
    REJECTED: t("common:status.rejected"),
  };

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        setCurrentPage(1);
        setSearchQuery("");
      };
    }, [])
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getDisplayedPages = (totalPages: number, currentPage: number) => {
    const pages = [];

    if (totalPages > 0) {
      pages.push(1);
    }

    if (currentPage > 4) {
      pages.push("...");
    }

    for (
      let i = Math.max(2, currentPage - 2);
      i <= Math.min(totalPages - 1, currentPage + 2);
      i++
    ) {
      pages.push(i);
    }

    if (currentPage < totalPages - 3) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const renderPageIndicator = ({ item }: { item: number }) => (
    <Pressable onPress={() => handlePageChange(item)} className="mx-1">
      <View
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentPage === item ? "bg-purple1" : "bg-gray"
        }`}
      >
        <Text
          className={`${currentPage === item ? "text-white" : "text-black1"}`}
        >
          {item}
        </Text>
      </View>
    </Pressable>
  );

  const filteredTransactions =
    props.data.filter(
      (transaction) =>
        transaction.id?.toLowerCase().includes(searchQuery?.toLowerCase())
    ) || [];

  const paginatedTransactions = (
    searchQuery ? filteredTransactions : props.data
  ).slice((currentPage - 1) * pageLimit, currentPage * pageLimit);

  const totalPages = Math.ceil(
    (searchQuery ? filteredTransactions : props.data).length / pageLimit
  );

  const checkTransactionValue = (transaction: any) => {
    if (transaction.exchangeType && !transaction.transactionType) {
      const precision = checkAssetPrecision(
        transaction.sourceDetails.assetTypeId
      );
      return formatToFloat(transaction.sourceDetails.amountToDebit, precision);
    } else if (transaction.transactionType === "INTERNAL_TRANSFER") {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "USD",
      }).format(formatToFloat(transaction.amountToDebit));
    } else if (transaction.transactionType && transaction.rail !== "CRYPTO") {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: "USD",
      }).format(formatToFloat(transaction.value));
    } else if (transaction.transactionType && transaction.rail === "CRYPTO") {
      return `${formatToFloat(transaction.value)} ${
        checkAsset(transaction.account?.assetTypeId) ?? ""
      }`;
    }
  };

  const checkTransactionType = (transaction: any): string => {
    if (transaction.exchangeType && !transaction.transactionType) {
      return "EXCHANGE";
    } else if (
      transaction.transactionType === "TRANSFER_OUT" ||
      transaction.transactionType === "FIAT_TRANSFER_OUT"
    ) {
      return "WITHDRAWAL";
    } else if (
      transaction.transactionType === "FIAT_TRANSFER_IN" ||
      transaction.transactionType === "TRANSFER_IN"
    ) {
      return "DEPOSIT";
    } else if (transaction.transactionType === "INTERNAL_TRANSFER") {
      return "INTERNAL TRANSFER";
    } else {
      return "TRANSFER";
    }
  };

  const checkTransactionCustomer = (
    transaction: any,
    type?: string
  ): string => {
    if (type === "SENDER") {
      if (transaction.exchangeType && !transaction.transactionType) {
        return transaction.sourceDetails?.sourceAccountId?.customer?.application
          ?.customerDetailsCorporation?.registeredName
          ? transaction.sourceDetails?.sourceAccountId?.customer?.application
              ?.customerDetailsCorporation?.registeredName
          : transaction.sourceDetails?.sourceAccountId?.customer?.application
              ?.customerDetailsIndividual?.firstName;
      } else if (
        transaction.transactionType === "FIAT_TRANSFER_IN" ||
        transaction.transactionType === "TRANSFER_IN"
      ) {
        return transaction.originator?.profile?.name ?? "-";
      } else if (
        transaction.transactionType === "TRANSFER_OUT" ||
        transaction.transactionType === "FIAT_TRANSFER_OUT"
      ) {
        return transaction.account?.customer?.application
          ?.customerDetailsCorporation?.registeredName
          ? transaction.account?.customer?.application
              ?.customerDetailsCorporation?.registeredName
          : transaction.account?.customer?.application
              ?.customerDetailsIndividual?.firstName;
      } else if (transaction.transactionType === "INTERNAL_TRANSFER") {
        return transaction.sourceFirstName
          ? `${transaction.sourceFirstName} ${
              transaction.sourceLastName ?? "-"
            }`
          : transaction.sourceRegisteredName;
      }
    } else {
      if (transaction.exchangeType && !transaction.transactionType) {
        return `${checkAsset(
          transaction.sourceDetails?.assetTypeId
        )} to ${checkAsset(transaction.destinationDetails?.assetTypeId)}`;
      } else if (
        transaction.transactionType === "TRANSFER_OUT" ||
        transaction.transactionType === "FIAT_TRANSFER_OUT"
      ) {
        return transaction.destinationCounterpartyId?.profile?.name ?? "-";
      } else if (
        transaction.transactionType === "TRANSFER_IN" ||
        transaction.transactionType === "FIAT_TRANSFER_IN"
      ) {
        return transaction.account?.customer?.application
          ?.customerDetailsCorporation?.registeredName
          ? transaction.account?.customer?.application
              ?.customerDetailsCorporation?.registeredName
          : transaction.account?.customer?.application
              ?.customerDetailsIndividual?.firstName;
      } else if (transaction.transactionType === "INTERNAL_TRANSFER") {
        return transaction.destinationFirstName
          ? `${transaction.destinationFirstName} ${
              transaction.sourceLastName ?? "-"
            }`
          : transaction.destinationRegisteredName;
      }
    }
  };

  return (
    <View className="w-full items-center">
      <View className="w-full items-start">
        <View className="absolute left-4 top-3">
          <AntDesign name="search1" size={16} color="#BDBDBD" />
        </View>
        <TextInput
          className="border border-midgray rounded-full pl-10 py-2 text-base"
          placeholder={t("common:terms.searchtransactionid")}
          placeholderTextColor="#BDBDBD"
          onChangeText={(text) => setSearchQuery(text)}
        />
      </View>
      <View className="w-full flex-row p-2 mt-4">
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.date")}
          </Text>
        </View>
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.sender")}
          </Text>
        </View>
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.receiver")}
          </Text>
        </View>
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.transfertype")}
          </Text>
        </View>
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.rail")}
          </Text>
        </View>
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.amount")}
          </Text>
        </View>
        <View className="w-1/7">
          <Text className="text-base font-medium">
            {t("common:placeholder.receipt")}
          </Text>
        </View>
      </View>
      <View className="w-full">
        <FlatList
          data={paginatedTransactions}
          renderItem={({ item }) => (
            <View className="bg-gray hover:bg-lightgray w-full flex-row mt-3 p-2 rounded-lg">
              <View className="w-1/7">
                <Text className="text-base">
                  {new Date(item.createdAt).toLocaleString(i18n.language, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    hour12: true,
                  })}
                </Text>
              </View>
              <View className="w-1/7">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >
                  {checkTransactionCustomer(item, "SENDER")}
                </Text>
              </View>
              <View className="w-1/7">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >
                  {checkTransactionCustomer(item, "RECEIVER")}
                </Text>
              </View>
              <View className="w-1/7">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >
                  {checkTransactionType(item)}
                </Text>
              </View>
              <View className="w-1/7">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >
                  {item.rail ?? "-"}
                </Text>
              </View>
              <View className="w-1/7">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="text-base"
                >
                  {checkTransactionValue(item)}
                </Text>
              </View>
              <View className="w-1/7">
                <Text className="text-base">
                  <Pressable
                    className="rounded-full py-1 px-1 bg-purple5 hover:bg-purple4"
                    onPress={() => {
                      setCurrentTransactionInfo(item);
                      setShowDetails(true);
                    }}
                  >
                    <Text className="text-xs text-white1">
                      {t("common:placeholder.details")}
                    </Text>
                  </Pressable>
                </Text>
              </View>
            </View>
          )}
        />
        <FlatList
          horizontal
          data={getDisplayedPages(totalPages, currentPage)}
          renderItem={renderPageIndicator}
          keyExtractor={(item, index) => index.toString()}
          contentContainerClassName="mt-4 w-full items-center justify-center"
        />
      </View>
      <AdminReceiptModal
        visible={showDetails}
        details={currentTransactionInfo}
        onClose={() => setShowDetails(false)}
        onClick={(state) => setShowDetails(state)}
      />
    </View>
  );
}
